import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { MaskLines } from "@/components/site/Reveal";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      const u = isLogin ? await login(form.email, form.password) : await register(form.name, form.email, form.password);
      toast.success(isLogin ? `Welcome back, ${u.name}` : "Account created — welcome!");
      nav(params.get("next") || (u.role === "admin" ? "/admin" : u.role === "driver" ? "/driver" : "/dashboard"));
    } catch (err) { setError(formatError(err)); } finally { setBusy(false); }
  };

  return (
    <main className="grid min-h-screen bg-sand lg:grid-cols-2">
      <div className="relative hidden overflow-hidden bg-ink lg:block">
        <img src="/img/cliffs.jpg" alt="Coastal cliffs above the ocean" className="h-full w-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-transparent" />
        <p className="absolute bottom-12 left-12 max-w-sm font-display text-4xl italic text-sand">Learning together, on Country.</p>
      </div>
      <div className="flex items-center px-5 pb-16 pt-32 sm:px-16">
        <form onSubmit={submit} data-testid={`${mode}-form`} className="w-full max-w-md">
          <p className="eyebrow text-ochre">{isLogin ? "Welcome back" : "Student account"}</p>
          <MaskLines as="h1" lines={[isLogin ? "Log in." : "Join GAC."]} className="mt-4 font-display text-6xl font-medium tracking-tight" />
          {!isLogin && <input data-testid="auth-name-input" required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="field mt-10" />}
          <input data-testid="auth-email-input" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`field ${isLogin ? "mt-10" : "mt-5"}`} />
          <input data-testid="auth-password-input" required type="password" minLength={6} placeholder="Password (min 6 characters)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="field mt-5" />
          {error && <p data-testid="auth-error" className="mt-5 text-sm text-red-700">{error}</p>}
          <button data-testid="auth-submit-button" disabled={busy} className="btn-ochre mt-10 w-full justify-center disabled:opacity-60">{busy ? "Please wait…" : isLogin ? "Log in" : "Create account"}</button>
          <p className="mt-8 text-sm text-ink/75">
            {isLogin ? "New here? " : "Already have an account? "}
            <Link to={`${isLogin ? "/register" : "/login"}${params.get("next") ? `?next=${params.get("next")}` : ""}`} data-testid="auth-switch-link" className="font-semibold text-ochre underline underline-offset-4">
              {isLogin ? "Create an account" : "Log in"}
            </Link>
          </p>
        </form>
      </div>
    </main>
  );
}
